import { logStartupBuildBanner } from '@lucimark/shared/helpers/logStartupBuildBanner'
import { createApp } from 'vue'
import App from '@/App.vue'
import { initPlayer } from '@/composables/initPlayer'
import usePlayer from '@/composables/usePlayer'
import { markBootSequenceReady, showBootSplashFailure, waitForAppPaint } from '@/helpers/bootSplash'
import { installMobilePageZoomSuppression } from '@/helpers/installMobilePageZoomSuppression'
import { maybePerformUnhandledErrorRecoveryReload } from '@/helpers/unhandledErrorRecoveryReload'
import { startWebKioskWakeLock } from '@/helpers/webKioskWakeLock'
import { registerWidgetServiceWorker } from '@/helpers/widgetSpaLoader'
import { getPlatformInstance, getPlatform, initPlatform } from '@/platform'
import { getLoggerService } from '@/services/logger'
import '@/assets/main.css'

installMobilePageZoomSuppression()

logStartupBuildBanner({
  appName: 'Lucimark Player',
  commitHash: __COMMIT_HASH__,
  branch: __COMMIT_BRANCH__,
  version: __APP_VERSION__,
  mode: import.meta.env.MODE
})

const { init } = usePlayer()
const logger = getLoggerService({ level: 'debug' })

/** Capture phase: distinguish failed script/stylesheet loads (common after OTA / cache skew) from runtime errors. */
window.addEventListener(
  'error',
  (ev) => {
    const t = ev.target
    if (t instanceof HTMLScriptElement && t.src) {
      logger.error(`Boot resource failed (script): ${t.src}`)
      showBootSplashFailure()
      return
    }
    if (t instanceof HTMLLinkElement && t.rel === 'stylesheet' && t.href) {
      logger.error(`Boot resource failed (stylesheet): ${t.href}`)
      showBootSplashFailure()
    }
  },
  true
)

window.addEventListener('error', (error) => {
  const ev = error as ErrorEvent
  if (ev.target instanceof HTMLScriptElement || (ev.target instanceof HTMLLinkElement && ev.target.rel === 'stylesheet')) {
    return
  }
  logger.error(`unhandled error {${error}}`)
  console.error('unhandled error', error)
  if (maybePerformUnhandledErrorRecoveryReload(ev.error ?? ev.message ?? error)) {
    logger.warn('unhandled error recovery reload scheduled')
  }
})

window.addEventListener('unhandledrejection', (event) => {
  const { type, reason } = event as PromiseRejectionEvent & { type: string }
  logger.error(`error {${type}} reason: {${reason}}`)
  if (maybePerformUnhandledErrorRecoveryReload(reason)) {
    console.warn('unhandled rejection recovery reload scheduled')
  }
})

/**
 * Log-only: recovery reload is owned by `window.addEventListener('error')` above.
 * Browsers often deliver both handlers for the same exception; dual recovery double-counted session reloads.
 */
window.onerror = (
  event: Event | string,
  source?: string,
  lineno?: number,
  colno?: number,
  error?: Error
) => {
  console.error({ source, lineno, colno, error, event })
}

initPlatform()
  .then(async () => {
    startWebKioskWakeLock()
    // P1a field smoke: expose bridge on Android so chrome://inspect can drive A/B without VBuffer.
    if (getPlatform() === 'android') {
      void import('@/helpers/androidHybridPlayback').then((m) => {
        ;(window as Window & { __lucimarkHybridPlayback?: typeof m }).__lucimarkHybridPlayback = m
      })
    }
    await usePlayer().startPlayerConnectivity()
    void initPlayer(logger)
    const app = createApp(App)
    return init(app).then(() => ({ app }))
  })
  .then(({ app }) => registerWidgetServiceWorker().then(() => app))
  .then((app) => {
    app.mount('#app')
    void usePlayer().runRegistrationBootstrap()
    return waitForAppPaint()
  })
  .then(() => {
    markBootSequenceReady()
    getPlatformInstance().requestFullscreen?.().catch(() => {})
  })
  .catch((e) => {
    const msg = e instanceof Error ? e.message : String(e)
    logger.error(`Player init failed: ${msg}`)
    console.error('Player init failed', e)
    showBootSplashFailure(msg)
  })
